import type { ReactNode } from 'react';

import { Icon } from '@coop/components';
import { useSetTimeout } from '@coop/hooks';
import { ChevronDownIcon, XIcon } from '@coop/icons';
import classNames from 'classnames';
import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import styles from './NoticeRibbon.module.scss';
import { NoticeRibbonType } from './NoticeRibbon.types';

type NotceRibbonAlign = 'left' | 'center';

interface NoticeRibbonProps {
    show: boolean;
    type?: NoticeRibbonType;
    animated?: boolean;
    extraCss?: string;
    onClick?: () => void;
    handleCloseClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
    hideAfterMiliseconds?: number; // set null if you want to disable automatic hide
    children?: React.ReactNode;
    expandableContent?: ReactNode;
    blockClose?: boolean;
    fixed?: boolean;
    align?: NotceRibbonAlign;
    rounded?: boolean;
    ariaLive?: 'assertive' | 'polite';
}

const NoticeRibbon = ({
    align = 'center',
    animated = true,
    ...props
}: React.PropsWithChildren<NoticeRibbonProps>) => {
    const containerStyles = classNames(
        styles.Notice,
        props.type === NoticeRibbonType.error && styles['Notice--error'],
        props.type === NoticeRibbonType.success && styles['Notice--success'],
        props.type === NoticeRibbonType.warning && styles['Notice--warning'],
        props.type === NoticeRibbonType.critical && [
            styles.NoticeAlert,
            styles['Notice--critical'],
        ],
        animated && styles['Notice--animated'],
        props.fixed && styles['Notice--fixed'],
        props.extraCss,
        props.onClick && 'u-cursorPointer',
    );

    const transitionRef = useRef(null);

    const [isExpanded, setIsExpanded] = useState(false);

    useSetTimeout(
        props.show && !!props.hideAfterMiliseconds && !!props.handleCloseClick,
        props.hideAfterMiliseconds!,
        () => props.handleCloseClick?.(),
    );

    const Content = useMemo(
        () => (
            <NoticeRibbonContent
                ref={transitionRef}
                containerStyles={containerStyles}
                onClick={props.onClick}
                handleCloseClick={
                    !props.hideAfterMiliseconds && !props.blockClose
                        ? props.handleCloseClick
                        : undefined
                }
                isExpanded={isExpanded}
                onExpandClick={() => setIsExpanded((prev) => !prev)}
                expandableContent={props.expandableContent}
                align={align}
            >
                {props.children}
            </NoticeRibbonContent>
        ),
        [
            containerStyles,
            props.onClick,
            props.hideAfterMiliseconds,
            props.blockClose,
            props.handleCloseClick,
            props.expandableContent,
            props.children,
            isExpanded,
            align,
        ],
    );

    if (!animated) {
        if (props.show) {
            return Content;
        }
        return null;
    }

    return (
        // Retrigger aria-live when the same text is shown multiple times. Without this, screen readers will not announce the same text if it is shown multiple times.
        <div aria-live={props.show ? props.ariaLive : 'off'}>
            <CSSTransition
                nodeRef={transitionRef}
                in={props.show}
                timeout={200}
                classNames={{
                    appearDone: styles['is-visible'],
                    enterDone: styles['is-visible'],
                }}
                appear
                unmountOnExit
            >
                {Content}
            </CSSTransition>
        </div>
    );
};

interface NoticeRibbonContentProps {
    containerStyles: string;
    onClick?: () => void;
    handleCloseClick?: () => void;
    children: ReactNode;
    isExpanded: boolean;
    expandableContent: ReactNode;
    onExpandClick: () => void;
    align?: NotceRibbonAlign;
}

const NoticeRibbonContent = React.forwardRef<HTMLDivElement, NoticeRibbonContentProps>(
    (props, ref) => {
        return (
            <div ref={ref} className={props.containerStyles} onClick={props.onClick}>
                <div
                    className={classNames(
                        styles.NoticeContent,
                        props.align === 'left' && styles['NoticeContent--alignLeft'],
                    )}
                >
                    {props.expandableContent && (
                        <button
                            type="button"
                            className={classNames(styles.NoticeHeading, styles.Expandable)}
                            onClick={props.onExpandClick}
                            aria-label="Visa mer"
                        >
                            {props.children}
                            <Icon
                                icon={<ChevronDownIcon />}
                                rotateClockwise={props.isExpanded ? '180deg' : '0deg'}
                                aria-hidden
                                size="medium"
                            />
                        </button>
                    )}

                    {!props.expandableContent &&
                        (typeof props.children === 'string' ? (
                            <div className={styles.NoticeHeading}>{props.children}</div>
                        ) : (
                            props.children
                        ))}

                    {props.handleCloseClick && (
                        <button
                            onClick={props.handleCloseClick}
                            type="button"
                            name="closebutton"
                            className={styles.NoticeClose}
                            aria-label="Stäng"
                        >
                            <Icon icon={<XIcon />} aria-hidden size="small" />
                        </button>
                    )}
                </div>
                {props.expandableContent && props.isExpanded && (
                    <div className={styles.NoticeExpandedContent}>{props.expandableContent}</div>
                )}
            </div>
        );
    },
);

export default NoticeRibbon;
